/**
 * Overworld hostiles, round 2 (vanilla Bedrock numbers, normal difficulty):
 *  - slime: size 1/2/4 -> 1/4/16 HP, damage 0/2/4, hops, splits into 2-4 halves
 *    on death, size-1 drops 0-2 slime balls, XP = size. Spawns in slime chunks
 *    (below y 40, see slimeChunk.ts) and in swamps.
 *  - cave spider: 12 HP, 0.7x spider, climbs, poison on hit (effect hook).
 *  - husk: zombie that does not burn; hunger on hit (effect hook). Deserts.
 *  - stray: skeleton of cold biomes; slowness arrows (effect hook, arrows do not
 *    carry effects yet).
 *  - drowned: zombie that swims; 11% copper ingot; 15% hold a trident and throw it (8 dmg, 8.5% drop).
 *  - silverfish: 8 HP, 1 damage. Infested blocks are not implemented.
 *  - endermite: 8 HP, 2 damage, despawns after 2 minutes; 5% from ender pearls.
 *
 * Effects (poison / hunger / slowness): `overworld2Hooks.hitEffect` is the
 * hook the EFFECTS workstream (src/game/effects) should wire to the player's
 * EffectSet. Until then hits only deal their direct damage.
 */

import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Hostile, Skeleton, Spider, Zombie } from '../hostile';
import { Mob, type MobCtx } from '../mob';
import { registerMobExtra } from '../persist';
import { Wolf } from './wolf';
import { Witch } from './witch';
import { Phantom } from './phantom';
import { Cat } from './cat';
import { Bat, Cod, Salmon, Squid } from './aquatic';
import { Panda, Parrot } from './jungle';
import { Fox } from './fox';

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

/** Status effects mobs inflict on the player, by name (ids live in src/game/effects). */
export type HitEffect = 'poison' | 'hunger' | 'slowness' | 'mining_fatigue' | 'wither' | 'levitation';

export const overworld2Hooks: {
  spawn: ((type: MobType, x: number, y: number, z: number) => Mob | null) | null;
  /** Apply a status effect to the player a mob just hit. */
  hitEffect?: (kind: HitEffect, ticks: number, amp: number) => void;
  /** Lower-case vanilla-ish biome name at a column ('desert', 'swamp', 'snowy plains'...). */
  biomeAt?: (x: number, z: number) => string;
} = { spawn: null };

// ------------------------------------------------------------------ slime

const SLIME_DMG: Record<number, number> = { 1: 0, 2: 2, 4: 4 };

/** Vanilla Slime.remove: split into 2 + nextInt(3) children (2..4) of half size. */
export const slimeSplitCount = (rnd: () => number): number => 2 + Math.floor(rnd() * 3);

export class Slime extends Hostile {
  /** 1, 2 or 4 (hitbox 0.52 * size). Mirrored to view.color for the renderer. */
  size = 4;
  private jumpDelay: number;
  private attackCd = 0;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'slime', x, y, z, { width: 2.08, height: 2.08, eye: 1.3, health: 16, speed: 0.2 }, rnd);
    this.noFallDamage = true;
    this.jumpDelay = 10 + Math.floor(rnd() * 20);
    // vanilla picks 1 << nextInt(3): sizes 1, 2, 4 evenly
    this.setSize(1 << Math.floor(rnd() * 3));
  }

  setSize(s: number): void {
    this.size = s;
    const w = 0.52 * s;
    const self = this as unknown as { width: number; height: number; eyeHeight: number; maxHealth: number };
    self.width = w; self.height = w; self.eyeHeight = w * 0.625;
    self.maxHealth = s * s;
    this.health = self.maxHealth;
    this.color = s;
  }

  override xpValue(): number { return this.size; }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    const p = ctx.host.player;
    const hunting = this.acquire(ctx, 16);
    if (hunting) {
      this.aggressive = true;
      this.faceToward(p.x, p.z);
      const dmg = SLIME_DMG[this.size] ?? 0;
      if (dmg > 0 && this.attackCd === 0 && this.inMeleeReach(ctx)) {
        this.attackCd = 20;
        ctx.hurtPlayer(this, dmg, 0.4);
      }
    }
    if (this.onGround) {
      if (this.swell > 0) this.swell = Math.max(0, this.swell - 0.15);
      if (this.jumpDelay > 0) { this.jumpDelay--; return; }
      this.jumpDelay = Math.floor(ctx.host.rnd() * 20) + (hunting ? 3 : 10);
      this.vy = 0.42;
      const a = hunting ? Math.atan2(p.z - this.z, p.x - this.x) : ctx.host.rnd() * Math.PI * 2;
      const sp = 0.1 + 0.05 * this.size;
      this.vx = Math.cos(a) * sp;
      this.vz = Math.sin(a) * sp;
      this.swell = 1; // squash anim
    }
  }

  override hurt(amount: number): boolean {
    const was = this.dying;
    const r = super.hurt(amount);
    if (!was && this.dying && this.size > 1 && overworld2Hooks.spawn) {
      const n = slimeSplitCount(Math.random);
      for (let i = 0; i < n; i++) {
        const ox = ((i % 2) - 0.5) * this.size * 0.25, oz = ((i >> 1) - 0.5) * this.size * 0.25;
        const c = overworld2Hooks.spawn('slime', this.x + ox, this.y + 0.5, this.z + oz);
        if (c instanceof Slime) c.setSize(this.size / 2);
      }
    }
    return r;
  }

  loot(rnd: () => number): Stack[] {
    if (this.size !== 1) return [];
    const n = roll(rnd, 0, 2);
    return n > 0 ? [stackOf(I.SLIME_BALL, n)] : [];
  }
}

registerMobExtra('slime', {
  save: (m) => [(m as Slime).size],
  load: (m, d) => {
    const s = (d as number[])[0];
    (m as Slime).setSize(s === 1 || s === 2 ? s : 4);
  },
});

// ------------------------------------------------------------ cave spider

export class CaveSpider extends Spider {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, x, y, z, rnd, 'cave_spider', { width: 0.7, height: 0.5, eye: 0.45, health: 12, speed: 0.3 });
    this.meleeDamage = 2;
  }
  protected override onMeleeHit(): void {
    // Normal difficulty: poison for 7 s
    overworld2Hooks.hitEffect?.('poison', 140, 0);
  }
}

// ------------------------------------------------------------------ husk

export class Husk extends Zombie {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, x, y, z, rnd, 'husk');
  }
  /** Husks do not burn in daylight. */
  protected override sunBurn(): void {}
  protected override ai(ctx: MobCtx): void {
    super.ai(ctx);
    // Zombie.ai swings on the tick it lands a hit
    if (this.swingTicks === 1) overworld2Hooks.hitEffect?.('hunger', 140, 0);
  }
}

// ----------------------------------------------------------------- stray

export class Stray extends Skeleton {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, x, y, z, rnd, 'stray');
  }
  // Slowness arrows: arrows carry no effect yet (hook: overworld2Hooks.hitEffect('slowness', 600, 0)).
}

// --------------------------------------------------------------- drowned

/** Vanilla: 15% of drowned spawn holding a trident (Bedrock), 8.5% of those drop it. */
export const DROWNED_TRIDENT_CHANCE = 0.15;
export const DROWNED_TRIDENT_DROP = 0.085;

export class Drowned extends Zombie {
  hasTrident = false;
  private tridentCd = 20;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, x, y, z, rnd, 'drowned');
    if (rnd() < DROWNED_TRIDENT_CHANCE) this.setTrident(true);
  }
  setTrident(on: boolean): void {
    this.hasTrident = on;
    this.heldItem = on ? I.TRIDENT : 0;
  }
  protected override ai(ctx: MobCtx): void {
    super.ai(ctx);
    // swim toward the target through water (vanilla DrownedGoToWater / swim goals)
    const p = ctx.host.player;
    if (this.inWater && this.hasTarget) {
      const dy = p.y + 0.5 - this.y;
      this.vy += Math.max(-0.03, Math.min(0.03, dy * 0.02));
    }
    if (this.tridentCd > 0) this.tridentCd--;
    if (!this.hasTrident || !this.hasTarget || !this.sees || this.tridentCd > 0 || !ctx.shootTrident) return;
    const dx = p.x - this.x, dz = p.z - this.z;
    const d = Math.hypot(dx, dz);
    if (d < 3 || d > 20) return;
    const sy = this.eyeY - 0.1;
    const dy = p.y + p.height * 0.333 - sy;
    // vanilla Drowned.performRangedAttack: aim a little high for the 0.05 gravity
    ctx.shootTrident(this.x, sy, this.z, dx, dy + d * 0.2, dz, 1.6, 6, this.id);
    this.tridentCd = 40;
    this.swing();
  }
  override loot(rnd: () => number, byPlayer: boolean): Stack[] {
    const out: Stack[] = [];
    const flesh = roll(rnd, 0, 2);
    if (flesh > 0) out.push(stackOf(I.ROTTEN_FLESH, flesh));
    if (byPlayer && rnd() < 0.11) out.push(stackOf(I.COPPER_INGOT, 1));
    if (this.hasTrident && rnd() < DROWNED_TRIDENT_DROP) out.push({ id: I.TRIDENT, count: 1, dmg: Math.floor(rnd() * 200) });
    return out;
  }
}

registerMobExtra('drowned', {
  save: (m) => [(m as Drowned).hasTrident ? 1 : 0],
  load: (m, d) => (m as Drowned).setTrident(((d as number[])[0] ?? 0) === 1),
});

// ------------------------------------------------- silverfish / endermite

abstract class Bug extends Hostile {
  protected attackCd = 0;
  constructor(id: number, type: MobType, x: number, y: number, z: number, rnd: () => number,
    health: number, private readonly damage: number) {
    super(id, type, x, y, z, { width: 0.4, height: 0.3, eye: 0.13, health, speed: 0.25 }, rnd);
  }
  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.acquire(ctx, 16)) {
      this.aggressive = true;
      this.chase(ctx, 1);
      if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
        this.attackCd = 20;
        this.swing();
        ctx.hurtPlayer(this, this.damage, 0.4);
      }
    } else {
      this.stroll(ctx, 0.8, 4, false);
      this.lookAtNearPlayer(ctx);
    }
  }
  loot(): Stack[] { return []; }
}

export class Silverfish extends Bug {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'silverfish', x, y, z, rnd, 8, 1);
  }
}

/** Endermites despawn two minutes after they appear. */
export const ENDERMITE_LIFETIME = 2400;

export class Endermite extends Bug {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'endermite', x, y, z, rnd, 8, 2);
  }
  override xpValue(): number { return 3; }
  protected override ai(ctx: MobCtx): void {
    if (this.age > ENDERMITE_LIFETIME) { this.removed = true; return; }
    super.ai(ctx);
  }
}

// ---------------------------------------------------------------- factory

export const OVERWORLD2_MOB_TYPES: readonly MobType[] = ['slime', 'cave_spider', 'husk', 'stray', 'drowned', 'silverfish', 'endermite', 'wolf', 'witch', 'phantom', 'cat', 'squid', 'cod', 'salmon', 'bat', 'panda', 'parrot', 'fox'];

export function createOverworld2Mob(type: MobType, id: number, x: number, y: number, z: number, rnd: () => number): Mob | null {
  switch (type) {
    case 'slime': return new Slime(id, x, y, z, rnd);
    case 'cave_spider': return new CaveSpider(id, x, y, z, rnd);
    case 'husk': return new Husk(id, x, y, z, rnd);
    case 'stray': return new Stray(id, x, y, z, rnd);
    case 'drowned': return new Drowned(id, x, y, z, rnd);
    case 'silverfish': return new Silverfish(id, x, y, z, rnd);
    case 'endermite': return new Endermite(id, x, y, z, rnd);
    case 'wolf': return new Wolf(id, x, y, z, rnd);
    case 'witch': return new Witch(id, x, y, z, rnd);
    case 'phantom': return new Phantom(id, x, y, z, rnd);
    case 'cat': return new Cat(id, x, y, z, rnd);
    case 'squid': return new Squid(id, x, y, z, rnd);
    case 'cod': return new Cod(id, x, y, z, rnd);
    case 'salmon': return new Salmon(id, x, y, z, rnd);
    case 'bat': return new Bat(id, x, y, z, rnd);
    case 'panda': return new Panda(id, x, y, z, rnd);
    case 'parrot': return new Parrot(id, x, y, z, rnd);
    case 'fox': return new Fox(id, x, y, z, rnd);
    default: return null;
  }
}

/**
 * Mob types a monster spawner block can hold (for STRUCTURES): spawn with
 * `entities.spawnMob(type, x, y, z)`; `createOverworld2Mob` / the manager
 * accept every name below. Mineshaft spawners use 'cave_spider', strongholds
 * 'silverfish'.
 */
export const SPAWNER_MOB_TYPES: readonly MobType[] = ['zombie', 'skeleton', 'spider', 'cave_spider', 'silverfish', 'slime', 'husk', 'stray', 'creeper'];
